import { ChangeDetectorRef, Component, Input, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ImportType } from "./import.component";
import { Bind } from "../../decorators/bind.decorator";
import { RowData } from "../s25-spreadsheet/types/sheet.js.types.expanded";
import { ContactImportRow, ContactImportSaveResponseDto, ImportService } from "./import.service";
import { ScrollLockService } from "../../services/scroll.lock.service";
import { ContactSpreadsheetUtil } from "./util/contact/contact.spreadsheet.util";
import { SpreadSheetService } from "../s25-spreadsheet/spreadsheet.service";
import { GroupService } from "../../services/group.service";
import { S25Util } from "../../util/s25-util";

@Component({
    selector: "s25-ng-import-contacts",
    template: `
        @if (init) {
            <div class="c-margin-bottom--single">
                Upload a single spreadsheet of contacts. Each row carries a contact's data and, optionally, a user
                (Username, Password, Security Group) plus any custom attribute columns. Contacts and users are matched
                (or created) by email; security groups must already exist and are matched by name.
            </div>

            <div class="c-margin-bottom--single">
                <label>
                    <input type="checkbox" [(ngModel)]="isStudentEngagement" />
                    Mark all contacts and users as Student Engagement
                </label>
            </div>

            <div class="c-margin-bottom--single c-margin-top--single">
                <button class="aw-button aw-button--outline" (click)="downloadTemplate()">
                    Download Excel Template
                </button>
            </div>
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule],
})
export class S25ImportContactsComponent implements OnInit {
    @Input() type: ImportType;
    @Input() parentCd?: ChangeDetectorRef;

    constructor(private cd: ChangeDetectorRef) {}

    init = false;
    isStudentEngagement = false;

    @Bind
    async onCompleteContacts(rows: RowData[]) {
        try {
            this.type.errors = [];
            this.type.successMessage = "";

            if (!rows || !rows.length) {
                this.type.errors.push("No sheet data found");
                this.refresh();
                return;
            }

            const errorSet = new Set<string>();
            const { contacts } = ContactSpreadsheetUtil.rowsToContacts(rows, errorSet);

            if (!contacts.length) {
                if (!errorSet.size) {
                    errorSet.add("No valid contacts found. Include an email column (and optional CA: columns).");
                }
                this.type.errors = Array.from(errorSet);
                this.refresh();
                return;
            }

            await this.resolveSecurityGroups(contacts, errorSet);
            this.validateContacts(contacts, errorSet);

            if (errorSet.size) {
                this.type.errors = Array.from(errorSet);
                this.refresh();
                return;
            }

            const response = await this.importContacts(contacts);
            if (response?.error && response.error !== "no_contacts") {
                this.type.errors.push("Error importing contacts: [" + response.error + "]");
            } else {
                if (response?.rowErrors?.length) {
                    this.type.errors.push(...response.rowErrors);
                }
                this.type.successMessage = this.buildSuccessMessage(response);
            }

            this.refresh();
        } catch (caughtError) {
            const errorData = caughtError?.data || caughtError?.error;
            if (errorData?.error) {
                this.type.errors.push(errorData.error);
            } else {
                S25Util.showError(caughtError, "Failed to import contacts");
            }
            this.refresh();
        }
    }

    /** Resolves each row's security group name to an id (groups are never auto-created). */
    async resolveSecurityGroups(contacts: ContactImportRow[], errorSet: Set<string>): Promise<void> {
        const hasGroups = contacts.some((contact) => !!contact.securityGroupName);
        if (!hasGroups) {
            return;
        }

        const groupItems = await GroupService.getGroupItems();
        const groupIdByName = new Map<string, number>();
        for (const group of groupItems) {
            if (group?.itemName) {
                groupIdByName.set(group.itemName.trim().toLowerCase(), group.itemId);
            }
        }

        for (const contact of contacts) {
            if (!contact.securityGroupName) {
                continue;
            }
            const groupId = groupIdByName.get(contact.securityGroupName.trim().toLowerCase());
            if (groupId === undefined) {
                errorSet.add(`Security group not found: "${contact.securityGroupName}"`);
            } else {
                contact.securityGroupId = groupId;
            }
            delete contact.securityGroupName;
        }
    }

    validateContacts(contacts: ContactImportRow[], errorSet: Set<string>): void {
        for (const contact of contacts) {
            if (contact.contId === undefined) {
                if (!contact.email?.trim()) {
                    errorSet.add("Contact row missing required field: email");
                }
                if (!contact.firstName?.trim()) {
                    errorSet.add("Contact row missing required field: firstName");
                }
                if (!contact.familyName?.trim()) {
                    errorSet.add("Contact row missing required field: familyName");
                }
            }

            this.validateFieldLength(errorSet, "Email", contact.email, 80);
            this.validateFieldLength(errorSet, "First name", contact.firstName, 40);
            this.validateFieldLength(errorSet, "Last name", contact.familyName, 40);
            this.validateFieldLength(errorSet, "Middle name", contact.additionalNames, 80);
            this.validateFieldLength(errorSet, "Name prefix", contact.namePrefix, 20);
            this.validateFieldLength(errorSet, "Name suffix", contact.nameSuffix, 20);
            this.validateFieldLength(errorSet, "Internal ID", contact.internalId, 30);
            this.validateFieldLength(errorSet, "Title", contact.contTitle, 40);
            this.validateFieldLength(errorSet, "Street address", contact.address, 150);
            this.validateFieldLength(errorSet, "City", contact.city, 40);
            this.validateFieldLength(errorSet, "State/Province", contact.stateProv, 40);
            this.validateFieldLength(errorSet, "Zip/Postal Code", contact.zipPostCode, 20);
            this.validateFieldLength(errorSet, "Country", contact.country, 40);
            this.validateFieldLength(errorSet, "Phone", contact.phone, 24);
            this.validateFieldLength(errorSet, "Fax", contact.fax, 24);
            this.validateFieldLength(errorSet, "Username", contact.r25Username, 40);
        }

        // Same email may appear on multiple rows, but first/last name must be consistent (org import pattern).
        const emailToFirstName = new Map<string, string>();
        const emailToFamilyName = new Map<string, string>();
        for (const contact of contacts) {
            if (!contact.email?.trim() || !contact.firstName?.trim() || !contact.familyName?.trim()) {
                continue;
            }
            const normalizedEmail = contact.email.trim().toLowerCase();
            const firstName = contact.firstName.trim();
            const familyName = contact.familyName.trim();

            const existingFirstName = emailToFirstName.get(normalizedEmail);
            const existingFamilyName = emailToFamilyName.get(normalizedEmail);

            if (existingFirstName && existingFirstName !== firstName) {
                errorSet.add(
                    `Contact email "${contact.email}" has conflicting first names: "${existingFirstName}" and "${firstName}"`,
                );
            }

            if (existingFamilyName && existingFamilyName !== familyName) {
                errorSet.add(
                    `Contact email "${contact.email}" has conflicting family names: "${existingFamilyName}" and "${familyName}"`,
                );
            }

            if (!existingFirstName) {
                emailToFirstName.set(normalizedEmail, firstName);
            }

            if (!existingFamilyName) {
                emailToFamilyName.set(normalizedEmail, familyName);
            }
        }

        const internalIdCounts = new Map<string, number>();
        for (const contact of contacts) {
            const normalizedInternalId = contact.internalId?.trim();
            if (!normalizedInternalId) {
                continue;
            }
            internalIdCounts.set(normalizedInternalId, (internalIdCounts.get(normalizedInternalId) ?? 0) + 1);
        }

        for (const [internalId, count] of internalIdCounts) {
            if (count > 1) {
                errorSet.add(`Duplicate Internal ID "${internalId}" appears ${count} times`);
            }
        }
    }

    private validateFieldLength(
        errorSet: Set<string>,
        fieldName: string,
        value: string | undefined | null,
        maxLength: number,
    ): void {
        if (value != null && value.length > maxLength) {
            errorSet.add(`${fieldName} cannot exceed ${maxLength} characters: "${value.substring(0, maxLength)}..."`);
        }
    }

    private buildSuccessMessage(response: ContactImportSaveResponseDto): string {
        const created = response?.createdContacts ?? 0;
        const updated = response?.updatedContacts ?? 0;
        const values = response?.customAttributeValuesWritten ?? 0;
        const createdUsers = response?.createdUsers ?? 0;
        const updatedUsers = response?.updatedUsers ?? 0;
        const groupsAssigned = response?.securityGroupsAssigned ?? 0;
        const newCas = response?.createdCustomAttributes ?? [];

        let message = `Imported contacts (${created} created, ${updated} matched), ${values} attribute value(s) written`;

        if (createdUsers || updatedUsers) {
            message += `. Users: ${createdUsers} created, ${updatedUsers} updated`;
        }

        if (groupsAssigned) {
            message += `, ${groupsAssigned} security group assignment(s)`;
        }

        if (newCas.length) {
            message += `. Created custom attributes: ${newCas.join(", ")}`;
        }

        return message;
    }

    async importContacts(contacts: ContactImportRow[]): Promise<ContactImportSaveResponseDto> {
        if (!contacts || !contacts.length) {
            this.type.errors.push("No valid contacts found");
            this.refresh();
            return { error: "no_contacts" };
        }
        return ImportService.importContacts({ contacts, isStudentEngagement: this.isStudentEngagement });
    }

    async ngOnInit() {
        this.type.onComplete = this.onCompleteContacts;
        this.init = true;
        this.refresh();
    }

    refresh() {
        this.cd.detectChanges();
        this.parentCd?.detectChanges();
        ScrollLockService.unlockBodyScroll();
    }

    async downloadTemplate() {
        const templateData = [
            {
                "Contact ID": "",
                "First Name": "Jordan",
                "Last Name": "Lee",
                "Middle Name": "Avery",
                "Name Prefix": "Ms.",
                "Name Suffix": "",
                "Internal ID": "SIS-12345",
                Title: "Student",
                Address: "123 College Ave",
                City: "Portland",
                State: "OR",
                Zip: "97201",
                Country: "USA",
                Phone: "503-555-0100",
                Fax: "",
                Email: "jordan.lee@example.edu",
                Username: "jlee",
                Password: "ChangeMe123",
                "Security Group": "Students",
                "CA: <S> Major": "Computer Science",
                "CA: <2> Class Standing": "Senior",
                "CA: <B> Veteran": "true",
                "CA: <N> Credits Earned": "98",
            },
        ];

        await SpreadSheetService.downloadExcel(templateData, "Contact_Import_Template.xlsx", "Contact Import Template");
    }
}
